import { Alert, Box, Button, Divider, IconButton, Modal } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { DetailsImageResponse } from "../models/ResponseModels/DetailsImageResponse";
import ErrorsString from "../ErrorsString";
import config from "../config";
import CloseIcon from "@mui/icons-material/Close";
import ImageCropDownload from "./ImageCropDownload";
import Spinner from "./Spinner";
import { PostShareModel } from "../models/PostShareModel";
import SharedUrlBox from "./SharedUrlBox";
import { saveAs } from "file-saver";
import { GetImageDetailsModel } from "../models/GetImageDetailsModel";
import StarIcon from "@mui/icons-material/Star";
//import TagList from "./TagList";
import { logDownload } from "../Utilities";
import { detailsSections } from "../configurations/imageDetailsSections";
import DetailField from "./DetailField";

interface Props {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  uuid: string | null;
  setSelectedUuid: React.Dispatch<React.SetStateAction<string>>;
}

export default function ImageDetailsModal(props: Props) {
  const [spinnerVisibility, setSpinnerVisibility] = useState(false);
  let [alertMessage, setAlertMessage] = useState<string>("");
  let [showModalAlert, setshowModalAlert] = useState<boolean>(false);
  let [alertShareMessage, setAlertShareMessage] = useState<string>("");
  let [showModalAlertShare, setshowModalAlertShare] = useState<boolean>(false);
  let [ready, setReady] = useState<boolean>(false);
  let [details, setDetails] = useState<DetailsImageResponse | null>(null);
  let [urlToShare, setUrlToShare] = useState<string | null>(null);

  const baseUrl = window.location.origin;

  function setError(message: string) {
    setshowModalAlert(true);
    setAlertMessage(message);
  }

  const getImageDetails = useCallback(() => {
    if (props.uuid == null) {
      setError(ErrorsString.missingUuid);
      return;
    }

    window.scrollTo(0, 0);
    setSpinnerVisibility(true);

    console.log("Starting API call GetImageDetailsModel...");
    (async () => {
      const response = await GetImageDetailsModel(props.uuid);
      // console.log( "GetImageDetailsModel response: " + JSON.stringify(response)  );

      const responseData = response?.data;
      const statusCode = response?.status;

      if (responseData === undefined || statusCode !== 200) {
        setError(ErrorsString.errorMessageGeneric);
        setSpinnerVisibility(false);
        return;
      }

      setshowModalAlert(false);
      setDetails(responseData);
      setSpinnerVisibility(false);

      setReady(true);
    })();
  }, [props.uuid]);

  useEffect(() => {
    setshowModalAlert(false);
    getImageDetails();
  }, [getImageDetails]);

  function handleClose() {
    setshowModalAlert(false);
    setReady(false);
    setDetails(null);
    setUrlToShare(null);
    props.setSelectedUuid(null);

    props.setOpenModal(false);
  }

  function share(imageUuid) {
    console.log("Starting API call PostShareModel...");
    (async () => {
      const response = await PostShareModel(imageUuid);
      //console.log("PostShareModel response: " + JSON.stringify(response));

      const responseData = response?.data;
      const statusCode = response?.status;

      if (responseData === undefined || statusCode !== 200) {
        setshowModalAlertShare(true);
        setAlertShareMessage("Error during share");
        return;
      }

      const encryptedString = responseData.encryptedString;
      const urlToShare = baseUrl + "/public/landing/" + encryptedString;
      //console.log("urlToShare: "+urlToShare);
      setUrlToShare(urlToShare);
    })();
  }

  function downloadOriginalImage() {
    if (!details?.essence) return;

    const url =
      config.cloudfrontImgUrl + "" + details.essence + config.showImagesQuery;
    let imageFileName = details.imageTitle;

    if (!details?.imageTitle) {
      const today = new Date();
      const formattedDate = today.toLocaleDateString();
      imageFileName = "image" + formattedDate;
    }

    const fileExtension = details.fileExtension ? details.fileExtension : "jpg";
    imageFileName = imageFileName + "." + fileExtension;

    saveAs(url, imageFileName);

    logDownload(details.uuid, null);
  }

  return (
    <>
      <Spinner visibility={spinnerVisibility} />
      {ready && (
        <Modal
          open={props.openModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            className="
            absolute 
            top-1/2 
            left-1/2 
            -translate-x-1/2 
            -translate-y-1/2             
            w-[90%]
            bg-white              
            px-0
            py-2
            md:p-4
            rounded
            "
          >
            <>
              <div
                id="modal-header"
                className=" h-8 w-full border-b border-gray-300 flex justify-between"
              >
                {details && (
                  <div>
                    <div className="font-skyBold text-xl line-clamp-1">
                      {details.woTitle}
                      <span className="font-skyMedium  text-gray-500 ">
                        {" - "}
                        {details.imageTitle}
                      </span>
                    </div>
                  </div>
                )}
                <div className="float-right">
                  <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              {showModalAlert && <Alert severity="error">{alertMessage}</Alert>}
              {details && (
                <div
                  id="image-body"
                  style={{
                    maxHeight: "80vh",
                    overflow: "auto",
                  }}
                >
                  <div id="share-info" className="text-lg mt-3">
                    <SharedUrlBox
                      url={urlToShare}
                      showError={showModalAlertShare}
                      errorMessage={alertShareMessage}
                    />
                  </div>

                  <div
                    id="actions"
                    className="mb-4 mt-4 mr-3 float-right flex justify-end w-full"
                  >
                    {details.isDownloadable && (
                      <div className="mr-3">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => downloadOriginalImage()}
                        >
                          Download Original File
                        </Button>
                      </div>
                    )}

                    {details.isShareable && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => share(details.uuid)}
                      >
                        Share
                      </Button>
                    )}
                  </div>

                  <div id="image-details" className="flex w-full mt-4">
                    <div id="image-preview" className="p-4 w-1/2 w-3/4">
                      <img
                        src={
                          details.proxy != null
                            ? config.cloudfrontImgUrl +
                              "" +
                              details.proxy +
                              config.showImagesQuery
                            : config.placeholderImageUrl
                        }
                        alt={details.imageTitle}
                      />
                    </div>
                    <div id="image-info" className=" p-4 w-full">
                      <div
                        className={`mt-auto mb-auto text-xl flex items-end font-skyRegular ${
                          details.isGreenStar && details.isGreenStar === true
                            ? "visible"
                            : "invisible"
                        } `}
                      >
                        <StarIcon
                          style={{ color: "green", marginRight: "5px" }}
                        />{" "}
                        Recommended by Editorial
                      </div>
                      <div id="details" className=" mt-8">
                        {detailsSections.map((section, sectionIndex) => (
                          <div key={sectionIndex}>
                            {sectionIndex > 0 && <Divider />}
                            <div className="p-2">
                              {section.fields.map((field) => (
                                <DetailField
                                  key={field.id}
                                  field={field}
                                  value={details[field.key]}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div id="image-crops">
                    <ImageCropDownload
                      uuid={details.uuid}
                      savedCrops={details.imageCropInfo}
                      realtivePath={details.essence}
                      imageDetails={details.imageDetails}
                      imageName={details.imageTitle}
                      fileName={details.fileName}
                      isDownloadable={details.isDownloadable}
                    ></ImageCropDownload>
                  </div>
                </div>
              )}
            </>
          </Box>
        </Modal>
      )}
    </>
  );
}
